import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, ArrowLeft, Loader2, GraduationCap, MapPin, Trash2, Edit2,
  CheckCircle2, AlertCircle, X, Calendar, Clock, Users, IndianRupee,
  ImageIcon, UserCircle2, ChevronRight, Share2, Copy, MessageCircle, Link as LinkIcon,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from '../hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import api from '../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SPORTS = [
  { id: 'football', label: 'Football', color: '#16a34a' },
  { id: 'padel', label: 'Padel', color: '#0ea5e9' },
  { id: 'pickleball', label: 'Pickleball', color: '#f59e0b' },
  { id: 'cricket', label: 'Cricket', color: '#dc2626' },
  { id: 'other', label: 'Other', color: '#6b7280' },
];
const LEVELS = [
  { id: 'beginner', label: 'Beginner' },
  { id: 'intermediate', label: 'Intermediate' },
  { id: 'advanced', label: 'Advanced' },
  { id: 'all_levels', label: 'All levels' },
];
const DAYS = [
  { id: 'monday', short: 'Mon' }, { id: 'tuesday', short: 'Tue' },
  { id: 'wednesday', short: 'Wed' }, { id: 'thursday', short: 'Thu' },
  { id: 'friday', short: 'Fri' }, { id: 'saturday', short: 'Sat' },
  { id: 'sunday', short: 'Sun' },
];
const DURATIONS = [30, 45, 60, 90, 120];
const SESSION_OPTIONS = [4, 6, 8, 10, 12, 'custom'];

const sportColor = (s) => SPORTS.find(x => x.id === s)?.color || '#6b7280';
const sportLabel = (s, other) => s === 'other' ? (other || 'Other') : (SPORTS.find(x => x.id === s)?.label || s);
const levelLabel = (l) => LEVELS.find(x => x.id === l)?.label || l;
const fmtDays = (days = []) => days.map(d => DAYS.find(x => x.id === d)?.short || d).join(' & ');
const fmtTime12 = (t) => {
  if (!t || !t.includes(':')) return t || '';
  const [h, m] = t.split(':'); const hi = parseInt(h, 10);
  const suf = hi < 12 ? 'AM' : 'PM'; const h12 = (hi % 12) || 12;
  return `${h12}:${m} ${suf}`;
};
const fmtDuration = (mins) => mins === 60 ? '1 hour' : mins === 90 ? '1.5 hours' : mins === 120 ? '2 hours' : `${mins} min`;

const statusPill = (s) => {
  const tones = {
    draft: 'bg-gray-100 text-gray-700',
    open: 'bg-emerald-100 text-emerald-700',
    full: 'bg-amber-100 text-amber-800',
    completed: 'bg-blue-100 text-blue-700',
    cancelled: 'bg-red-100 text-red-700',
  };
  return tones[s] || 'bg-gray-100 text-gray-700';
};

// ============== Share (reuses the tournament share pattern) ==============

const PUBLIC_BASE = 'https://playspaces.in';
const publicCoachingUrl = (slug) => `${PUBLIC_BASE}/coaching/${slug}`;

const copyToClipboard = async (text) => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    const el = document.createElement('textarea');
    el.value = text;
    el.style.position = 'fixed';
    el.style.left = '-9999px';
    document.body.appendChild(el);
    el.focus(); el.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(el);
    return ok;
  } catch (_) {
    return false;
  }
};

const ShareProgrammeModal = ({ open, onClose, programme }) => {
  if (!programme) return null;
  const title = programme.title || 'Coaching Programme';
  const url = publicCoachingUrl(programme.slug);
  const message = `Join ${title} — coaching details & registration:\n${url}`;
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(url)}`;

  const copyMessage = async () => {
    const ok = await copyToClipboard(message);
    toast({ title: ok ? 'Message copied' : 'Copy not allowed by browser' });
  };
  const copyLink = async () => {
    const ok = await copyToClipboard(url);
    toast({ title: ok ? 'Link copied' : 'Copy not allowed by browser' });
  };
  const waHref = `https://wa.me/?text=${encodeURIComponent(message)}`;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="share-coaching-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5 text-emerald-600" /> Share Coaching Programme
          </DialogTitle>
          <DialogDescription>
            Share the public page. Anyone can open it (no login needed) to see details and register.
          </DialogDescription>
        </DialogHeader>

        <div>
          <div className="text-[11px] uppercase font-semibold text-gray-500 tracking-wide mb-1">Public URL</div>
          <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 break-all font-mono" data-testid="share-coaching-url">
            {url}
          </div>
        </div>

        <div>
          <div className="text-[11px] uppercase font-semibold text-gray-500 tracking-wide mb-1">Message to share</div>
          <pre className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700 whitespace-pre-wrap font-sans" data-testid="share-coaching-message">
{message}
          </pre>
        </div>

        <div className="flex flex-col items-center gap-2">
          <div className="text-[11px] uppercase font-semibold text-gray-500 tracking-wide">Scan to open</div>
          <div className="rounded-lg border border-gray-200 bg-white p-2">
            <img
              src={qrSrc}
              alt="QR code for coaching programme"
              width={200}
              height={200}
              className="block"
              data-testid="share-coaching-qr"
            />
          </div>
        </div>

        <DialogFooter className="flex flex-col gap-2 sm:flex-col">
          <Button onClick={copyMessage} className="w-full" data-testid="share-coaching-copy-message">
            <Copy className="h-4 w-4 mr-2" /> Copy Message
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={copyLink} className="flex-1" data-testid="share-coaching-copy-link">
              <LinkIcon className="h-4 w-4 mr-2" /> Copy Link Only
            </Button>
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 inline-flex items-center justify-center gap-2 h-10 px-4 rounded-md bg-[#25D366] text-white text-sm font-medium hover:bg-[#20bd5a] transition"
              data-testid="share-coaching-whatsapp"
            >
              <MessageCircle className="h-4 w-4" /> WhatsApp
            </a>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const EditProgrammeModal = ({ programme, onClose, onSaved }) => {
  const [form, setForm] = useState({
    title: programme.title || '',
    description: programme.description || '',
    sport: programme.sport || 'football',
    other_sport_name: programme.other_sport_name || '',
    level: programme.level || 'beginner',
    days_of_week: programme.days_of_week || [],
    start_time: programme.start_time || '',
    session_duration_minutes: programme.session_duration_minutes || 60,
    coach_name: programme.coach_name || '',
    coach_bio: programme.coach_bio || '',
    banner_image_url: programme.banner_image_url || '',
    coach_photo_url: programme.coach_photo_url || '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [bannerBusy, setBannerBusy] = useState(false);
  const [coachBusy, setCoachBusy] = useState(false);

  const uploadImage = async (endpoint, file, setBusy, key) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || 'Upload failed');
      }
      const { url } = await res.json();
      setForm(p => ({ ...p, [key]: url }));
      toast({ title: 'Image uploaded' });
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const update = (patch) => setForm(p => ({ ...p, ...patch }));
  const toggleDay = (d) => setForm(p => ({
    ...p,
    days_of_week: p.days_of_week.includes(d) ? p.days_of_week.filter(x => x !== d) : [...p.days_of_week, d],
  }));

  const save = async () => {
    if (!form.title.trim()) { setErr('Title is required'); return; }
    if (!form.days_of_week.length) { setErr('Select at least one day'); return; }
    if (!form.start_time) { setErr('Start time is required'); return; }
    if (form.sport === 'other' && !form.other_sport_name.trim()) { setErr('Enter the sport name'); return; }
    setSaving(true);
    setErr('');
    try {
      await api.request(`/coaching/admin/programmes/${programme.id}`, {
        method: 'PATCH',
        body: JSON.stringify(form),
      });
      toast({ title: 'Programme updated' });
      onSaved();
    } catch (e) {
      setErr(e.message || 'Could not save changes');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-programme-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit2 className="h-5 w-5 text-[#0A2C93]" /> Edit Programme
          </DialogTitle>
          <DialogDescription>Update the programme details. Changes reflect on the public page immediately.</DialogDescription>
        </DialogHeader>

        {err && <div className="bg-red-50 border border-red-200 rounded-lg p-2.5 text-xs text-red-700" data-testid="edit-programme-error">{err}</div>}

        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-gray-600">Title</label>
            <input value={form.title} onChange={e => update({ title: e.target.value })}
              className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" data-testid="edit-programme-title" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600">Description</label>
            <textarea value={form.description} onChange={e => update({ description: e.target.value })} rows={4}
              className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" data-testid="edit-programme-description" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600">Banner image</label>
            <div className="mt-1">
              {form.banner_image_url ? (
                <div className="relative">
                  <img src={`${API_URL}${form.banner_image_url}`} alt="banner" className="w-full aspect-[3/1] object-cover rounded-lg border" data-testid="edit-banner-preview" />
                  <label className="absolute bottom-2 right-2 inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-white/90 rounded-lg text-xs font-medium shadow cursor-pointer hover:bg-white" data-testid="edit-banner-upload">
                    <input type="file" accept="image/*" className="hidden"
                      onChange={e => { const f = e.target.files?.[0]; if (f) uploadImage('/api/coaching/admin/upload-banner', f, setBannerBusy, 'banner_image_url'); }}
                      data-testid="edit-banner-input" />
                    {bannerBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImageIcon className="h-3.5 w-3.5" />}
                    {bannerBusy ? 'Uploading…' : 'Replace'}
                  </label>
                  <button type="button" onClick={() => update({ banner_image_url: '' })}
                    className="absolute top-2 right-2 bg-white/90 rounded-full p-1 shadow" data-testid="edit-banner-remove">
                    <X className="h-4 w-4 text-red-600" />
                  </button>
                </div>
              ) : (
                <label className="inline-flex items-center gap-2 px-3 py-2 border rounded-lg text-sm cursor-pointer hover:bg-gray-50" data-testid="edit-banner-upload">
                  <input type="file" accept="image/*" className="hidden"
                    onChange={e => { const f = e.target.files?.[0]; if (f) uploadImage('/api/coaching/admin/upload-banner', f, setBannerBusy, 'banner_image_url'); }}
                    data-testid="edit-banner-input" />
                  {bannerBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}
                  {bannerBusy ? 'Uploading…' : 'Upload banner'}
                </label>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600">Sport</label>
              <select value={form.sport} onChange={e => update({ sport: e.target.value })}
                className="mt-1 w-full border rounded-lg px-3 py-2 text-sm bg-white" data-testid="edit-programme-sport">
                {SPORTS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600">Level</label>
              <select value={form.level} onChange={e => update({ level: e.target.value })}
                className="mt-1 w-full border rounded-lg px-3 py-2 text-sm bg-white" data-testid="edit-programme-level">
                {LEVELS.map(l => <option key={l.id} value={l.id}>{l.label}</option>)}
              </select>
            </div>
          </div>
          {form.sport === 'other' && (
            <div>
              <label className="text-xs font-semibold text-gray-600">Sport name</label>
              <input value={form.other_sport_name} onChange={e => update({ other_sport_name: e.target.value })}
                className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" data-testid="edit-programme-other-sport" />
            </div>
          )}
          <div>
            <label className="text-xs font-semibold text-gray-600">Days of week</label>
            <div className="flex flex-wrap gap-2 mt-1" data-testid="edit-programme-days">
              {DAYS.map(d => (
                <button key={d.id} type="button" onClick={() => toggleDay(d.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border ${form.days_of_week.includes(d.id) ? 'bg-[#0A2C93] text-white border-[#0A2C93]' : 'bg-white text-gray-600'}`}
                  data-testid={`edit-day-${d.id}`}>
                  {d.short}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600">Start time</label>
              <input type="time" value={form.start_time} onChange={e => update({ start_time: e.target.value })}
                className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" data-testid="edit-programme-start" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600">Session duration</label>
              <select value={form.session_duration_minutes} onChange={e => update({ session_duration_minutes: parseInt(e.target.value, 10) })}
                className="mt-1 w-full border rounded-lg px-3 py-2 text-sm bg-white" data-testid="edit-programme-duration">
                {DURATIONS.map(d => <option key={d} value={d}>{fmtDuration(d)}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600">Coach name</label>
            <input value={form.coach_name} onChange={e => update({ coach_name: e.target.value })}
              className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" data-testid="edit-programme-coach-name" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600">Coach bio</label>
            <textarea value={form.coach_bio} onChange={e => update({ coach_bio: e.target.value })} rows={2} maxLength={300}
              className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" data-testid="edit-programme-coach-bio" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600">Coach photo</label>
            <div className="mt-1 flex items-center gap-3">
              {form.coach_photo_url && (
                <img src={`${API_URL}${form.coach_photo_url}`} alt="coach" className="h-14 w-14 rounded-full object-cover border" data-testid="edit-coach-photo-preview" />
              )}
              <label className="inline-flex items-center gap-2 px-3 py-2 border rounded-lg text-sm cursor-pointer hover:bg-gray-50" data-testid="edit-coach-photo-upload">
                <input type="file" accept="image/*" className="hidden"
                  onChange={e => { const f = e.target.files?.[0]; if (f) uploadImage('/api/coaching/admin/upload-coach-photo', f, setCoachBusy, 'coach_photo_url'); }}
                  data-testid="edit-coach-photo-input" />
                {coachBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}
                {coachBusy ? 'Uploading…' : (form.coach_photo_url ? 'Replace photo' : 'Upload photo')}
              </label>
              {form.coach_photo_url && (
                <button type="button" onClick={() => update({ coach_photo_url: '' })} className="text-xs text-red-600 hover:underline" data-testid="edit-coach-photo-remove">Remove</button>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1" data-testid="edit-programme-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="flex-1" data-testid="edit-programme-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============== Top-level component ==============

const CoachingManagement = () => {
  const [view, setView] = useState('list'); // 'list' | 'create' | 'detail'
  const [programmes, setProgrammes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.request('/coaching/admin/programmes');
      setProgrammes(Array.isArray(data) ? data : []);
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (view === 'create') {
    return <CreateProgrammeWizard
      onCancel={() => setView('list')}
      onCreated={(id) => { setActiveId(id); setView('detail'); load(); }}
    />;
  }
  if (view === 'detail' && activeId) {
    return <ProgrammeDetail
      programmeId={activeId}
      onBack={() => { setActiveId(null); setView('list'); load(); }}
    />;
  }
  return <ProgrammesList
    programmes={programmes}
    loading={loading}
    onCreate={() => setView('create')}
    onOpen={(id) => { setActiveId(id); setView('detail'); }}
  />;
};

// ============== Listing ==============

const ProgrammesList = ({ programmes, loading, onCreate, onOpen }) => {
  const [statusFilter, setStatusFilter] = useState('all');
  const filtered = statusFilter === 'all' ? programmes : programmes.filter(p => p.status === statusFilter);
  return (
    <div className="p-4 pt-16 lg:p-6 lg:pt-6 pb-28 lg:pb-6 max-w-5xl mx-auto" data-testid="coaching-list">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl lg:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <GraduationCap className="h-6 w-6 text-[#0A2C93]" /> Coaching
          </h1>
          <p className="text-sm text-gray-500 mt-1">Programmes &amp; package registrations</p>
        </div>
        <button
          onClick={onCreate}
          className="flex items-center gap-1.5 bg-[#0A2C93] text-white px-4 py-2 rounded-lg font-semibold text-sm hover:bg-[#082273]"
          data-testid="create-programme-btn"
        >
          <Plus className="h-4 w-4" /> Create
        </button>
      </div>

      <div className="flex items-center gap-2 overflow-x-auto pb-2 mb-4 no-scrollbar" data-testid="status-filter">
        {['all', 'draft', 'open', 'full', 'completed', 'cancelled'].map(s => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold capitalize ${
              statusFilter === s ? 'bg-gray-900 text-white' : 'bg-white border text-gray-600'
            }`}
            data-testid={`filter-${s}`}
          >
            {s}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border p-10 text-center">
          <GraduationCap className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">No programmes yet</p>
          <p className="text-xs text-gray-400 mt-1">Click Create to publish your first coaching programme.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(p => (
            <button
              key={p.id}
              onClick={() => onOpen(p.id)}
              className="text-left bg-white border rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow flex flex-col"
              data-testid={`programme-card-${p.id}`}
            >
              <div className="aspect-video relative" style={{ backgroundColor: sportColor(p.sport) }}>
                {p.banner_image_url ? (
                  <img src={`${API_URL}${p.banner_image_url}`} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <GraduationCap className="h-10 w-10 text-white/70" />
                  </div>
                )}
                <span className={`absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${statusPill(p.status)}`}>
                  {p.status}
                </span>
              </div>
              <div className="p-4 flex flex-col gap-1.5 flex-1">
                <h3 className="font-bold text-base text-gray-900 line-clamp-2 leading-tight">{p.title}</h3>
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <MapPin className="h-3 w-3" />{p.venue_name}
                </p>
                <p className="text-xs text-gray-500">
                  {sportLabel(p.sport, p.other_sport_name)} · {levelLabel(p.level)}
                </p>
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <Calendar className="h-3 w-3" />{fmtDays(p.days_of_week)} · {fmtTime12(p.start_time)}
                </p>
                <div className="flex items-center justify-between mt-1 pt-2 border-t">
                  <span className="text-xs text-gray-500">{p.total_registrations || 0} registered</span>
                  <span className="text-xs font-semibold text-gray-900">₹{(p.total_revenue || 0).toFixed(0)}</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ============== Create Wizard ==============

const blankPackage = () => ({
  label: '',
  number_of_sessions: 4,
  custom_sessions: '',
  price: '',
  max_spots: '',
});

const CreateProgrammeWizard = ({ onCancel, onCreated }) => {
  const { user, isSuperAdmin } = useAuth();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [facilities, setFacilities] = useState([]);
  const [bannerUploading, setBannerUploading] = useState(false);
  const [coachUploading, setCoachUploading] = useState(false);
  const [bannerError, setBannerError] = useState('');

  const [form, setForm] = useState({
    title: '',
    description: '',
    venue_id: user?.facility_id || '',
    banner_image_url: '',
    sport: 'football',
    other_sport_name: '',
    level: 'beginner',
    days_of_week: [],
    start_time: '',
    session_duration_minutes: 60,
    coach_name: '',
    coach_bio: '',
    coach_photo_url: '',
    packages: [blankPackage()],
  });

  useEffect(() => {
    if (isSuperAdmin) {
      api.request('/facilities').then(d => setFacilities(d || [])).catch(() => {});
    }
  }, [isSuperAdmin]);

  const update = (patch) => setForm(p => ({ ...p, ...patch }));
  const toggleDay = (d) => setForm(p => ({
    ...p,
    days_of_week: p.days_of_week.includes(d)
      ? p.days_of_week.filter(x => x !== d)
      : [...p.days_of_week, d],
  }));
  const updatePkg = (idx, patch) =>
    setForm(p => ({ ...p, packages: p.packages.map((pk, i) => i === idx ? { ...pk, ...patch } : pk) }));
  const addPkg = () => setForm(p => ({ ...p, packages: [...p.packages, blankPackage()] }));
  const removePkg = (idx) => setForm(p => ({ ...p, packages: p.packages.filter((_, i) => i !== idx) }));

  const uploadImage = async (endpoint, file, setBusy, onUrl) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Upload failed');
      }
      const { url } = await res.json();
      onUrl(url);
      toast({ title: 'Uploaded', description: 'Preview is below.' });
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const handleBanner = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBannerError('');
    uploadImage('/api/coaching/admin/upload-banner', file, setBannerUploading,
      (url) => update({ banner_image_url: url }));
  };
  const handleCoachPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    uploadImage('/api/coaching/admin/upload-coach-photo', file, setCoachUploading,
      (url) => update({ coach_photo_url: url }));
  };

  const canNext1 = () =>
    form.title.trim() && form.venue_id && form.sport && form.level
    && (form.sport !== 'other' || form.other_sport_name.trim())
    && form.days_of_week.length > 0 && form.start_time && form.session_duration_minutes;
  const canNext3 = () => form.packages.length >= 1 && form.packages.length <= 5 && form.packages.every(pk =>
    pk.label.trim() && pk.number_of_sessions
    && (pk.number_of_sessions !== 'custom' || (Number(pk.custom_sessions) >= 1))
    && pk.price !== '' && Number(pk.price) >= 0
    && Number(pk.max_spots) >= 1
  );

  const buildPayload = (publish) => ({
    title: form.title.trim(),
    description: form.description.trim() || null,
    venue_id: form.venue_id,
    banner_image_url: form.banner_image_url || null,
    sport: form.sport,
    other_sport_name: form.sport === 'other' ? form.other_sport_name.trim() : null,
    level: form.level,
    days_of_week: form.days_of_week,
    start_time: form.start_time,
    session_duration_minutes: Number(form.session_duration_minutes),
    coach_name: form.coach_name.trim() || null,
    coach_bio: form.coach_bio.trim() || null,
    coach_photo_url: form.coach_photo_url || null,
    publish,
    packages: form.packages.map(pk => ({
      label: pk.label.trim(),
      number_of_sessions: pk.number_of_sessions,
      custom_sessions: pk.number_of_sessions === 'custom' ? Number(pk.custom_sessions) : null,
      price: Number(pk.price) || 0,
      max_spots: Number(pk.max_spots),
    })),
  });

  const save = async (publish) => {
    if (publish && !form.banner_image_url) {
      setStep(1);
      setBannerError('Please upload a banner image before publishing');
      toast({ title: 'Banner required', description: 'Please upload a banner image before publishing', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const res = await api.request('/coaching/admin/programmes', {
        method: 'POST', body: JSON.stringify(buildPayload(publish)),
      });
      toast({ title: publish ? 'Programme published' : 'Draft saved', description: `Created "${form.title}"` });
      onCreated(res.programme.id);
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 pt-16 lg:p-6 lg:pt-6 pb-28 lg:pb-6 max-w-3xl mx-auto" data-testid="create-programme-wizard">
      <button onClick={onCancel} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-4" data-testid="back-to-list">
        <ArrowLeft className="h-4 w-4" /> Back to Coaching
      </button>
      <h1 className="text-xl lg:text-2xl font-bold text-gray-900 mb-2">Create coaching programme</h1>

      {/* Stepper */}
      <div className="flex items-center gap-2 mb-6 text-xs">
        {[1, 2, 3, 4].map(n => (
          <React.Fragment key={n}>
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full ${step === n ? 'bg-[#0A2C93] text-white' : 'bg-gray-100 text-gray-500'}`}>
              <span className="font-bold">{n}</span>
              <span className="hidden sm:inline">{['Programme', 'Coach (optional)', 'Packages', 'Review'][n - 1]}</span>
            </div>
            {n < 4 && <div className="flex-1 h-px bg-gray-200" />}
          </React.Fragment>
        ))}
      </div>

      {/* STEP 1 — Programme basics */}
      {step === 1 && (
        <div className="space-y-5 bg-white rounded-2xl border p-5">
          <Field label="Programme title" required>
            <input
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0A2C93]/30"
              value={form.title}
              onChange={e => update({ title: e.target.value })}
              placeholder="e.g. Pickleball Beginners Programme"
              data-testid="programme-title"
            />
          </Field>
          <Field label="Description">
            <textarea
              rows={3}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0A2C93]/30"
              value={form.description}
              onChange={e => update({ description: e.target.value })}
              data-testid="programme-description"
            />
          </Field>
          <Field label="Venue" required>
            {isSuperAdmin ? (
              <select
                className="w-full px-3 py-2 border rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0A2C93]/30"
                value={form.venue_id}
                onChange={e => update({ venue_id: e.target.value })}
                data-testid="programme-venue"
              >
                <option value="">Select venue…</option>
                {facilities.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            ) : (
              <input
                value={user?.facility_name || form.venue_id}
                disabled
                className="w-full px-3 py-2 border rounded-lg text-sm bg-gray-50 text-gray-600"
              />
            )}
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Sport" required>
              <select
                className="w-full px-3 py-2 border rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0A2C93]/30"
                value={form.sport}
                onChange={e => update({ sport: e.target.value })}
                data-testid="programme-sport"
              >
                {SPORTS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </Field>
            <Field label="Level" required>
              <select
                className="w-full px-3 py-2 border rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0A2C93]/30"
                value={form.level}
                onChange={e => update({ level: e.target.value })}
                data-testid="programme-level"
              >
                {LEVELS.map(l => <option key={l.id} value={l.id}>{l.label}</option>)}
              </select>
            </Field>
          </div>

          {form.sport === 'other' && (
            <Field label="Sport name" required>
              <input
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0A2C93]/30"
                value={form.other_sport_name}
                onChange={e => update({ other_sport_name: e.target.value })}
                placeholder="e.g. Squash"
                data-testid="programme-other-sport"
              />
            </Field>
          )}

          <Field label="Days of week" required helper="Select all days the programme runs (multi-select)">
            <div className="flex flex-wrap gap-2" data-testid="programme-days">
              {DAYS.map(d => (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => toggleDay(d.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                    form.days_of_week.includes(d.id)
                      ? 'bg-[#0A2C93] text-white border-[#0A2C93]'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid={`day-${d.id}`}
                >
                  {d.short}
                </button>
              ))}
            </div>
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Start time" required>
              <input
                type="time"
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0A2C93]/30"
                value={form.start_time}
                onChange={e => update({ start_time: e.target.value })}
                data-testid="programme-start-time"
              />
            </Field>
            <Field label="Session duration" required>
              <select
                className="w-full px-3 py-2 border rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0A2C93]/30"
                value={form.session_duration_minutes}
                onChange={e => update({ session_duration_minutes: Number(e.target.value) })}
                data-testid="programme-duration"
              >
                {DURATIONS.map(d => <option key={d} value={d}>{fmtDuration(d)}</option>)}
              </select>
            </Field>
          </div>

          <Field label="Banner image" helper="16:9 recommended. Required before publishing.">
            <div className={`border-2 border-dashed rounded-xl p-4 ${bannerError ? 'border-red-300 bg-red-50' : 'border-gray-200'}`} data-testid="banner-zone">
              {form.banner_image_url ? (
                <div className="space-y-3">
                  <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden">
                    <img src={`${API_URL}${form.banner_image_url}`} alt="banner preview" className="w-full h-full object-cover" data-testid="banner-preview" />
                  </div>
                  <button
                    onClick={() => { update({ banner_image_url: '' }); setBannerError(''); }}
                    className="text-xs text-red-600 hover:underline flex items-center gap-1"
                    data-testid="banner-remove"
                  >
                    <Trash2 className="h-3 w-3" /> Remove
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Sport-coloured placeholder */}
                  <div className="aspect-video rounded-lg flex items-center justify-center"
                       style={{ backgroundColor: sportColor(form.sport) }} data-testid="banner-placeholder">
                    <GraduationCap className="h-10 w-10 text-white/70" />
                  </div>
                  <label className="block">
                    <input type="file" accept="image/*" onChange={handleBanner} className="hidden" data-testid="banner-input" />
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 border rounded-lg text-xs font-semibold cursor-pointer hover:bg-gray-50">
                      {bannerUploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImageIcon className="h-3.5 w-3.5" />}
                      Upload banner
                    </span>
                  </label>
                </div>
              )}
              {bannerError && (
                <p className="mt-3 text-xs text-red-600 flex items-center gap-1" data-testid="banner-error">
                  <AlertCircle className="h-3.5 w-3.5" /> {bannerError}
                </p>
              )}
            </div>
          </Field>

          <div className="flex justify-end pt-2">
            <button
              onClick={() => setStep(2)}
              disabled={!canNext1()}
              className="px-5 py-2 rounded-lg bg-[#0A2C93] text-white text-sm font-semibold disabled:opacity-40"
              data-testid="step1-next"
            >
              Next: Coach details
            </button>
          </div>
        </div>
      )}

      {/* STEP 2 — Coach (optional) */}
      {step === 2 && (
        <div className="space-y-5 bg-white rounded-2xl border p-5" data-testid="step2-coach">
          <div className="flex items-start gap-3 bg-blue-50 border border-blue-100 rounded-lg p-3">
            <UserCircle2 className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-sm text-gray-900">Coach details (optional)</h3>
              <p className="text-xs text-gray-600 mt-0.5">Leave blank if you don't want to show coach information to customers</p>
            </div>
          </div>

          <Field label="Coach name">
            <input
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0A2C93]/30"
              value={form.coach_name}
              onChange={e => update({ coach_name: e.target.value })}
              placeholder="e.g. Coach Arjun"
              data-testid="coach-name"
            />
          </Field>
          <Field label="Coach bio" helper={`Max 300 characters · ${form.coach_bio.length}/300`}>
            <textarea
              rows={3}
              maxLength={300}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0A2C93]/30"
              value={form.coach_bio}
              onChange={e => update({ coach_bio: e.target.value })}
              data-testid="coach-bio"
            />
          </Field>
          <Field label="Coach photo">
            <div className="border-2 border-dashed border-gray-200 rounded-xl p-4">
              {form.coach_photo_url ? (
                <div className="flex items-center gap-3">
                  <img src={`${API_URL}${form.coach_photo_url}`} alt="coach" className="h-16 w-16 rounded-full object-cover" data-testid="coach-photo-preview" />
                  <button onClick={() => update({ coach_photo_url: '' })} className="text-xs text-red-600 hover:underline flex items-center gap-1">
                    <Trash2 className="h-3 w-3" /> Remove
                  </button>
                </div>
              ) : (
                <label className="block">
                  <input type="file" accept="image/*" onChange={handleCoachPhoto} className="hidden" data-testid="coach-photo-input" />
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 border rounded-lg text-xs font-semibold cursor-pointer hover:bg-gray-50">
                    {coachUploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImageIcon className="h-3.5 w-3.5" />}
                    Upload photo
                  </span>
                </label>
              )}
            </div>
          </Field>
          <div className="flex justify-between pt-2">
            <button onClick={() => setStep(1)} className="px-5 py-2 rounded-lg border text-sm font-semibold text-gray-600" data-testid="step2-back">Back</button>
            <button onClick={() => setStep(3)} className="px-5 py-2 rounded-lg bg-[#0A2C93] text-white text-sm font-semibold" data-testid="step2-next">
              Next: Packages
            </button>
          </div>
        </div>
      )}

      {/* STEP 3 — Packages */}
      {step === 3 && (
        <div className="space-y-4" data-testid="step3-packages">
          <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 text-xs text-amber-900">
            <strong>Tip:</strong> Add 1 to 5 packages. Each package represents a complete bundle the customer buys upfront.
          </div>
          {form.packages.map((pk, idx) => (
            <div key={idx} className="bg-white rounded-2xl border p-5 space-y-3" data-testid={`package-row-${idx}`}>
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-sm font-semibold text-gray-900">Package {idx + 1}</h3>
                {form.packages.length > 1 && (
                  <button onClick={() => removePkg(idx)} className="text-xs text-red-600 hover:underline flex items-center gap-1" data-testid={`pkg-remove-${idx}`}>
                    <Trash2 className="h-3 w-3" /> Remove
                  </button>
                )}
              </div>
              <Field label="Package label" required helper='e.g. "4 sessions", "Starter pack"'>
                <input
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0A2C93]/30"
                  value={pk.label}
                  onChange={e => updatePkg(idx, { label: e.target.value })}
                  data-testid={`pkg-label-${idx}`}
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Number of sessions" required>
                  <select
                    className="w-full px-3 py-2 border rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0A2C93]/30"
                    value={pk.number_of_sessions}
                    onChange={e => updatePkg(idx, { number_of_sessions: e.target.value === 'custom' ? 'custom' : Number(e.target.value) })}
                    data-testid={`pkg-sessions-${idx}`}
                  >
                    {SESSION_OPTIONS.map(s => <option key={s} value={s}>{s === 'custom' ? 'Custom' : `${s} sessions`}</option>)}
                  </select>
                </Field>
                {pk.number_of_sessions === 'custom' && (
                  <Field label="Custom sessions" required>
                    <input
                      type="number"
                      min={1}
                      className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0A2C93]/30"
                      value={pk.custom_sessions}
                      onChange={e => updatePkg(idx, { custom_sessions: e.target.value })}
                      data-testid={`pkg-custom-sessions-${idx}`}
                    />
                  </Field>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Total price for full package (₹)" required helper="Total cost the customer pays once for all sessions in this package">
                  <input
                    type="number"
                    min={0}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0A2C93]/30"
                    value={pk.price}
                    onChange={e => updatePkg(idx, { price: e.target.value })}
                    placeholder="e.g. 4000"
                    data-testid={`pkg-price-${idx}`}
                  />
                </Field>
                <Field label="Max spots" required>
                  <input
                    type="number"
                    min={1}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0A2C93]/30"
                    value={pk.max_spots}
                    onChange={e => updatePkg(idx, { max_spots: e.target.value })}
                    data-testid={`pkg-max-spots-${idx}`}
                  />
                </Field>
              </div>
            </div>
          ))}
          {form.packages.length < 5 && (
            <button onClick={addPkg} className="w-full py-3 border-2 border-dashed border-gray-200 rounded-xl text-sm font-semibold text-gray-600 hover:border-gray-300 flex items-center justify-center gap-1" data-testid="add-package">
              <Plus className="h-4 w-4" /> Add another package
            </button>
          )}
          <div className="flex justify-between pt-2">
            <button onClick={() => setStep(2)} className="px-5 py-2 rounded-lg border text-sm font-semibold text-gray-600" data-testid="step3-back">Back</button>
            <button onClick={() => setStep(4)} disabled={!canNext3()} className="px-5 py-2 rounded-lg bg-[#0A2C93] text-white text-sm font-semibold disabled:opacity-40" data-testid="step3-next">
              Next: Review
            </button>
          </div>
        </div>
      )}

      {/* STEP 4 — Review */}
      {step === 4 && (
        <div className="space-y-4 bg-white rounded-2xl border p-5" data-testid="step4-review">
          <h3 className="text-base font-semibold text-gray-900">Review</h3>
          {form.banner_image_url ? (
            <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden">
              <img src={`${API_URL}${form.banner_image_url}`} alt="" className="w-full h-full object-cover" />
            </div>
          ) : (
            <div className="aspect-video rounded-lg flex items-center justify-center" style={{ backgroundColor: sportColor(form.sport) }}>
              <GraduationCap className="h-10 w-10 text-white/70" />
            </div>
          )}
          <div>
            <h4 className="font-bold text-lg text-gray-900">{form.title}</h4>
            <p className="text-xs text-gray-500 mt-0.5">{sportLabel(form.sport, form.other_sport_name)} · {levelLabel(form.level)} · {fmtDays(form.days_of_week)} · {fmtTime12(form.start_time)} · {fmtDuration(form.session_duration_minutes)}</p>
          </div>
          {form.description && <p className="text-sm text-gray-600 whitespace-pre-line">{form.description}</p>}
          {form.coach_name && (
            <div className="border rounded-lg p-3 flex items-center gap-3">
              {form.coach_photo_url && (
                <img src={`${API_URL}${form.coach_photo_url}`} alt="" className="h-12 w-12 rounded-full object-cover" />
              )}
              <div>
                <p className="text-sm font-semibold">{form.coach_name}</p>
                {form.coach_bio && <p className="text-xs text-gray-500 mt-0.5">{form.coach_bio}</p>}
              </div>
            </div>
          )}
          <div>
            <p className="text-xs font-semibold text-gray-700 uppercase mb-2">Packages</p>
            <div className="space-y-2">
              {form.packages.map((pk, idx) => (
                <div key={idx} className="border rounded-lg p-3 flex items-center justify-between text-sm">
                  <div>
                    <p className="font-semibold">{pk.label}</p>
                    <p className="text-xs text-gray-500">
                      {pk.number_of_sessions === 'custom' ? `${pk.custom_sessions} sessions` : `${pk.number_of_sessions} sessions`} · {pk.max_spots} spots
                    </p>
                  </div>
                  <p className="font-bold">₹{pk.price || 0}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="flex justify-between pt-2 gap-2">
            <button onClick={() => setStep(3)} className="px-4 py-2 rounded-lg border text-sm font-semibold text-gray-600" data-testid="step4-back">Back</button>
            <div className="flex gap-2">
              <button onClick={() => save(false)} disabled={saving} className="px-4 py-2 rounded-lg border text-sm font-semibold text-gray-700 disabled:opacity-40" data-testid="save-draft">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save as Draft'}
              </button>
              <button onClick={() => save(true)} disabled={saving} className="px-4 py-2 rounded-lg bg-[#00D650] text-[#0A2C93] text-sm font-semibold disabled:opacity-40" data-testid="publish-programme">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Publish'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============== Programme Detail / Management ==============

const ProgrammeDetail = ({ programmeId, onBack }) => {
  const [pr, setPr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [activePackageId, setActivePackageId] = useState(null);
  const [showAddReg, setShowAddReg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.request(`/coaching/admin/programmes/${programmeId}`);
      setPr(data);
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [programmeId]);

  useEffect(() => { load(); }, [load]);

  const action = async (path, label) => {
    if (!window.confirm(`${label} this programme?`)) return;
    setBusy(true);
    try {
      await api.request(`/coaching/admin/programmes/${programmeId}${path}`, { method: 'POST' });
      toast({ title: `${label} successful` });
      load();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const cancelPackage = async (pkgId, label) => {
    if (!window.confirm(`Cancel package "${label}"? All active registrations will be refunded.`)) return;
    setBusy(true);
    try {
      await api.request(`/coaching/admin/programmes/${programmeId}/packages/${pkgId}/cancel`, { method: 'POST' });
      toast({ title: 'Package cancelled', description: 'Active registrations refunded.' });
      load();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const cancelRegistration = async (reg) => {
    if (!window.confirm(`Cancel registration for ${reg.player_name}? Full refund will be processed.`)) return;
    setBusy(true);
    try {
      await api.request(`/coaching/admin/registrations/${reg.id}/cancel`, { method: 'POST' });
      toast({ title: 'Registration cancelled' });
      load();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>;
  }
  if (!pr) {
    return <div className="p-6">Programme not found <button onClick={onBack} className="underline ml-2">Back</button></div>;
  }

  const allRegs = (pr.packages || []).flatMap(p => (p.registrations || []).map(r => ({ ...r, _pkg_label: p.label, _pkg_id: p.id })));
  const filteredRegs = activePackageId ? allRegs.filter(r => r._pkg_id === activePackageId) : allRegs;

  return (
    <div className="p-4 pt-16 lg:p-6 lg:pt-6 pb-28 lg:pb-6 max-w-5xl mx-auto" data-testid="programme-detail">
      <button onClick={onBack} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-4" data-testid="back-to-list">
        <ArrowLeft className="h-4 w-4" /> Back to Coaching
      </button>

      <ShareProgrammeModal open={shareOpen} onClose={() => setShareOpen(false)} programme={pr} />
      {editOpen && (
        <EditProgrammeModal
          programme={pr}
          onClose={() => setEditOpen(false)}
          onSaved={() => { setEditOpen(false); load(); }}
        />
      )}

      {/* Header */}
      <div className="bg-white rounded-2xl border overflow-hidden mb-5">
        <div className="aspect-[3/1] relative" style={{ backgroundColor: sportColor(pr.sport) }}>
          {pr.banner_image_url ? (
            <img src={`${API_URL}${pr.banner_image_url}`} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <GraduationCap className="h-12 w-12 text-white/70" />
            </div>
          )}
        </div>
        <div className="p-5">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h1 className="text-xl font-bold text-gray-900" data-testid="programme-title">{pr.title}</h1>
              <p className="text-xs text-gray-500 mt-1">
                {pr.venue_name} · {sportLabel(pr.sport, pr.other_sport_name)} · {levelLabel(pr.level)}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                {fmtDays(pr.days_of_week)} · {fmtTime12(pr.start_time)} · {fmtDuration(pr.session_duration_minutes)}
              </p>
            </div>
            <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase ${statusPill(pr.status)}`}>{pr.status}</span>
          </div>
          {pr.description && <p className="text-sm text-gray-600 mt-3 whitespace-pre-line">{pr.description}</p>}
          {pr.coach_name && (
            <div className="mt-4 flex items-center gap-3 pt-3 border-t">
              {pr.coach_photo_url && <img src={`${API_URL}${pr.coach_photo_url}`} alt="" className="h-10 w-10 rounded-full object-cover" />}
              <div>
                <p className="text-sm font-semibold">{pr.coach_name}</p>
                {pr.coach_bio && <p className="text-xs text-gray-500">{pr.coach_bio}</p>}
              </div>
            </div>
          )}
          {/* Status actions */}
          <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t" data-testid="programme-actions">
            {pr.status !== 'cancelled' && (
              <button onClick={() => setShareOpen(true)} className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-semibold inline-flex items-center gap-1.5" data-testid="action-share">
                <Share2 className="h-3.5 w-3.5" /> Share
              </button>
            )}
            {pr.status !== 'cancelled' && (
              <button onClick={() => setEditOpen(true)} className="px-3 py-1.5 rounded-lg bg-[#0A2C93] text-white text-xs font-semibold inline-flex items-center gap-1.5" data-testid="action-edit">
                <Edit2 className="h-3.5 w-3.5" /> Edit
              </button>
            )}
            {pr.status === 'draft' && (
              <button onClick={() => action('/publish', 'Publish')} disabled={busy} className="px-3 py-1.5 rounded-lg bg-[#00D650] text-[#0A2C93] text-xs font-semibold disabled:opacity-40" data-testid="action-publish">Publish</button>
            )}
            {(pr.status === 'open' || pr.status === 'full') && (
              <button onClick={() => action('/complete', 'Mark completed')} disabled={busy} className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-semibold disabled:opacity-40" data-testid="action-complete">Mark Completed</button>
            )}
            {pr.status !== 'cancelled' && pr.status !== 'completed' && (
              <button onClick={() => action('/cancel', 'Cancel')} disabled={busy} className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-xs font-semibold disabled:opacity-40" data-testid="action-cancel">Cancel programme</button>
            )}
          </div>
        </div>
      </div>

      {/* Packages breakdown */}
      <div className="bg-white rounded-2xl border p-5 mb-5" data-testid="packages-breakdown">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Packages</h3>
        <div className="space-y-2">
          {(pr.packages || []).map(pkg => {
            const max = pkg.max_spots || 0;
            const reg = pkg.current_registrations || 0;
            const revenue = reg * (pkg.price || 0);
            const pct = max > 0 ? Math.min(100, Math.round((reg / max) * 100)) : 0;
            return (
              <div key={pkg.id} className="border rounded-lg p-3" data-testid={`pkg-row-${pkg.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold">{pkg.label}</p>
                    <p className="text-xs text-gray-500">
                      {pkg.sessions_total} sessions · ₹{pkg.price} · {reg}/{max} registered
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-gray-900">₹{revenue.toFixed(0)} revenue</span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${statusPill(pkg.status)}`}>{pkg.status}</span>
                  </div>
                </div>
                <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-[#0A2C93]" style={{ width: `${pct}%` }} />
                </div>
                <div className="flex gap-2 mt-2.5 flex-wrap">
                  {pkg.status !== 'cancelled' && pr.status !== 'cancelled' && (
                    <>
                      <button
                        onClick={() => setShowAddReg(pkg.id)}
                        className="text-xs text-[#0A2C93] hover:underline flex items-center gap-1"
                        data-testid={`add-reg-${pkg.id}`}
                      >
                        <Plus className="h-3 w-3" /> Add registration manually
                      </button>
                      <button
                        onClick={() => cancelPackage(pkg.id, pkg.label)}
                        disabled={busy}
                        className="text-xs text-red-600 hover:underline flex items-center gap-1"
                        data-testid={`cancel-pkg-${pkg.id}`}
                      >
                        Cancel package
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Registrations table */}
      <div className="bg-white rounded-2xl border p-5" data-testid="registrations-section">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="text-sm font-semibold text-gray-900">Registrations ({allRegs.length})</h3>
          <select
            value={activePackageId || ''}
            onChange={e => setActivePackageId(e.target.value || null)}
            className="px-3 py-1.5 border rounded-lg text-xs bg-white"
            data-testid="filter-by-package"
          >
            <option value="">All packages</option>
            {(pr.packages || []).map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
        </div>
        {filteredRegs.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-8">No registrations yet</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 uppercase">
                  <th className="py-2 pr-3">Player</th>
                  <th className="py-2 pr-3">Phone</th>
                  <th className="py-2 pr-3">Package</th>
                  <th className="py-2 pr-3">Amount</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2"></th>
                </tr>
              </thead>
              <tbody>
                {filteredRegs.map(r => (
                  <tr key={r.id} className="border-t" data-testid={`reg-row-${r.id}`}>
                    <td className="py-2 pr-3">{r.player_name}</td>
                    <td className="py-2 pr-3 text-gray-500">{r.player_phone}</td>
                    <td className="py-2 pr-3 text-gray-500">{r._pkg_label}</td>
                    <td className="py-2 pr-3">₹{(r.amount_paid || 0).toFixed(0)}</td>
                    <td className="py-2 pr-3">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                        r.status === 'cancelled' ? 'bg-red-100 text-red-700'
                          : r.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700'
                          : r.payment_status === 'manual' ? 'bg-blue-100 text-blue-700'
                          : 'bg-amber-100 text-amber-800'
                      }`}>
                        {r.status === 'cancelled' ? 'cancelled' : r.payment_status}
                      </span>
                    </td>
                    <td className="py-2 text-right">
                      {r.status === 'active' && (
                        <button onClick={() => cancelRegistration(r)} disabled={busy} className="text-xs text-red-600 hover:underline" data-testid={`cancel-reg-${r.id}`}>
                          Cancel
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showAddReg && (
        <AddRegistrationModal
          programmeId={programmeId}
          packageId={showAddReg}
          packageLabel={(pr.packages || []).find(p => p.id === showAddReg)?.label || ''}
          onClose={() => setShowAddReg(null)}
          onSaved={() => { setShowAddReg(null); load(); }}
        />
      )}
    </div>
  );
};

// ============== Add Registration Modal ==============

const AddRegistrationModal = ({ programmeId, packageId, packageLabel, onClose, onSaved }) => {
  const [form, setForm] = useState({ name: '', phone: '', email: '' });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.name.trim() || !form.phone.trim()) {
      toast({ title: 'Missing fields', description: 'Name and phone are required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await api.request(`/coaching/admin/programmes/${programmeId}/packages/${packageId}/add-registration`, {
        method: 'POST', body: JSON.stringify({
          name: form.name.trim(), phone: form.phone.trim(),
          email: form.email.trim() || null,
        }),
      });
      toast({ title: 'Registration added', description: `${form.name} added to ${packageLabel}` });
      onSaved();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="add-reg-modal">
      <div className="bg-white rounded-2xl max-w-md w-full p-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold">Add registration manually</h3>
          <button onClick={onClose}><X className="h-5 w-5 text-gray-400" /></button>
        </div>
        <p className="text-xs text-gray-500 mb-4">Package: <strong>{packageLabel}</strong>. No payment will be collected.</p>
        <div className="space-y-3">
          <Field label="Name" required>
            <input className="w-full px-3 py-2 border rounded-lg text-sm" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="manual-reg-name" />
          </Field>
          <Field label="Phone" required>
            <input className="w-full px-3 py-2 border rounded-lg text-sm" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} data-testid="manual-reg-phone" />
          </Field>
          <Field label="Email">
            <input type="email" className="w-full px-3 py-2 border rounded-lg text-sm" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} data-testid="manual-reg-email" />
          </Field>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border text-sm font-semibold text-gray-600">Cancel</button>
          <button onClick={submit} disabled={saving} className="px-4 py-2 rounded-lg bg-[#0A2C93] text-white text-sm font-semibold disabled:opacity-40" data-testid="manual-reg-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add registration'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============== Shared ==============

const Field = ({ label, required, helper, children }) => (
  <label className="block">
    <div className="text-xs font-semibold text-gray-700 mb-1">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </div>
    {children}
    {helper && <p className="text-[11px] text-gray-500 mt-1">{helper}</p>}
  </label>
);

export default CoachingManagement;
